import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import type { MeResponse } from '@stonelink/shared';
import { api } from './api';
import { LoginPage } from './pages/Login';
import { SignupPage } from './pages/Signup';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { TeamPage } from './pages/Team';
import { PlatformAdminPage } from './pages/PlatformAdmin';
import { TrainPage } from './pages/Train';
import { NotebookPage } from './pages/Notebook';
import { GapsPage } from './pages/Gaps';
import { ConversationsPage } from './pages/Conversations';
import { AppearancePage } from './pages/Appearance';
import { AccountDataPage } from './pages/AccountData';

/** `ownerOnly` items are hidden from staff — the API refuses them regardless. */
const NAV = [
  { to: '/train', label: 'Train', ownerOnly: false },
  { to: '/notebook', label: 'Notebook', ownerOnly: false },
  { to: '/gaps', label: 'Gaps', ownerOnly: false },
  { to: '/conversations', label: 'Conversations', ownerOnly: false },
  { to: '/account-data', label: 'Account data', ownerOnly: true },
  { to: '/appearance', label: 'Appearance & Embed', ownerOnly: true },
  { to: '/team', label: 'Team', ownerOnly: true },
] as const;

export function App() {
  const [session, setSession] = useState<MeResponse | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // A valid session cookie means we can skip the login screen entirely.
    void api
      .me()
      .then(setSession)
      .catch(() => setSession(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <p className="font-mono text-xs tracking-[0.2em] text-muted uppercase">Loading…</p>
      </div>
    );
  }

  if (!session) {
    // Signed out, the console is still two pages: sign in, and sign up.
    return (
      <Routes>
        <Route path="/signup" element={<SignupPage onSignedIn={setSession} />} />
        <Route path="/accept-invite" element={<AcceptInvitePage onSignedIn={setSession} />} />
        <Route path="*" element={<LoginPage onSignedIn={setSession} />} />
      </Routes>
    );
  }

  const isOwner = session.user.role === 'owner';
  const isPlatformAdmin = session.user.platformAdmin;

  const signOut = async () => {
    await api.logout();
    setSession(null);
  };

  return (
    <div className="flex min-h-full">
      <aside className="flex w-60 flex-none flex-col border-r border-line bg-glass backdrop-blur-xl">
        <div className="border-b border-line px-5 py-5">
          <p className="font-display text-2xl leading-none">
            Stone<em className="text-brand italic">Link</em>
          </p>
          <p className="mt-1 font-mono text-[10px] tracking-[0.22em] text-muted uppercase">
            Assist
          </p>
          <p className="mt-4 truncate text-sm font-medium">{session.tenant.name}</p>
          <p className="truncate text-xs text-muted">{session.user.email}</p>
        </div>

        <nav className="flex-1 space-y-0.5 p-3" aria-label="Console sections">
          {NAV.filter((item) => isOwner || !item.ownerOnly).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `block rounded-xl px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? 'border border-brand/30 bg-brand/12 text-brand'
                    : 'border border-transparent text-muted hover:bg-white/5 hover:text-ink'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}

          {isPlatformAdmin && (
            <NavLink
              to="/platform"
              className={({ isActive }) =>
                `mt-3 block rounded-xl border-l-2 border-brand/50 px-3 py-2 text-sm font-medium transition ${
                  isActive ? 'bg-brand/12 text-brand' : 'text-muted hover:bg-white/5 hover:text-ink'
                }`
              }
            >
              Platform
            </NavLink>
          )}
        </nav>

        <div className="border-t border-line p-3">
          <button
            type="button"
            onClick={() => void signOut()}
            className="w-full rounded-xl px-3 py-2 text-left text-sm text-muted transition hover:bg-white/5 hover:text-ink"
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto p-8">
        <div className="mx-auto flex h-full max-w-5xl flex-col">
          <Routes>
            <Route path="/train" element={<TrainPage />} />
            <Route path="/notebook" element={<NotebookPage />} />
            <Route path="/gaps" element={<GapsPage />} />
            <Route path="/conversations" element={<ConversationsPage />} />
            {isOwner && <Route path="/account-data" element={<AccountDataPage />} />}
            {isOwner && <Route path="/appearance" element={<AppearancePage />} />}
            {isOwner && <Route path="/team" element={<TeamPage />} />}
            {isPlatformAdmin && <Route path="/platform" element={<PlatformAdminPage />} />}
            <Route path="*" element={<Navigate to="/train" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
